import { createSelector } from '@reduxjs/toolkit';
import { createStructuredSelector } from 'reselect';
import { UnreadMessagesFilter } from '../../../helper/channelOrderingHelper';
import { CHANNEL_FILTER_TYPES } from '../../../helper/channelTypeHelper';
import { getChannelListChannelIds, getChannels } from '../../channels/channelsSelectors';
import { getIsChatConnected } from '../../chatConnection/chatConnectionSelectors';
import { getCurrentUser, getIsAgent } from '../../currentUser/currentUserSelectors';
import { getChannelMembers, getMembers } from '../../members/membersSelectors';
import { getSalesSpaceUnreadMessagesFilterSelected } from '../../salesSpaceChannels/salesSpaceChannelsSelectors/getSalesSpaceUnreadMessagesFilterSelected';
import { getSelectedSalesSpaceChannelFilter } from '../../salesSpaceChannels/salesSpaceChannelsSelectors/getSelectedSalesSpaceChannelFilter';
import { getSelectedSalesSpaceChannelOrder } from '../../salesSpaceChannels/salesSpaceChannelsSelectors/getSelectedSalesSpaceChannelOrder';

import { getChannelNotificationDates } from '../../tickets/ticketsSelectors';
import { getUsers } from '../../users/usersSelectors';
import {
  filterByBrand,
  filterByHiddenStatus,
  filterBySearch,
  filterBySelectedChannelTypeFilter,
  filterBySelectedSalesSpaceChannelFilter,
  filterByUnreadMessages,
} from '../channelFilter';
import sortChannels from '../channelSorter';
import { getSearchInput } from './getSearchInput';
import { getSelectedChannelBrand } from './getSelectedChannelBrand';
import { getSelectedChannelOrder } from './getSelectedChannelOrder';
import { getSelectedChannelTypeFilter } from './getSelectedChannelTypeFilter';
import { getUnreadMessagesFilterSelected } from './getUnreadMessagesFilterSelected';

export const getDisplayChannelsIds = createSelector(
  createStructuredSelector({
    allChannelIds: getChannelListChannelIds,
    searchInput: getSearchInput,
    selectedDirectChannelOrder: getSelectedChannelOrder,
    selectedSalesSpaceChannelOrder: getSelectedSalesSpaceChannelOrder,
    selectedChannelBrand: getSelectedChannelBrand,
    selectedChannelTypeFilter: getSelectedChannelTypeFilter,
    selectedSalesSpaceChannelFilter: getSelectedSalesSpaceChannelFilter,
    unreadMessagesFilterSelected: getUnreadMessagesFilterSelected,
    unreadSalesSpaceMessagesFilterSelected: getSalesSpaceUnreadMessagesFilterSelected,
    currentUser: getCurrentUser,
    channelMembers: getChannelMembers,
    members: getMembers,
    users: getUsers,
    isAgent: getIsAgent,
    channels: getChannels,
    isConnected: getIsChatConnected,
    channelNotificationDates: getChannelNotificationDates,
  }),
  ({
    allChannelIds,
    searchInput,
    selectedDirectChannelOrder,
    selectedSalesSpaceChannelOrder,
    selectedChannelBrand,
    selectedChannelTypeFilter,
    selectedSalesSpaceChannelFilter,
    unreadMessagesFilterSelected,
    unreadSalesSpaceMessagesFilterSelected,
    currentUser,
    channelMembers,
    members,
    users,
    isAgent,
    channels,
    isConnected,
    channelNotificationDates,
  }) => {
    if (!allChannelIds) {
      return [];
    }

    const currentUserId = currentUser?.id;

    // Channel title or subtitle must contain search string (case independent).
    let filteredChannelIds = allChannelIds.filter(
      filterBySearch({
        searchInput,
        channels,
        currentUserId,
        channelMembers,
        members,
        users,
        isAgent,
      }),
    );

    if (isAgent) {
      filteredChannelIds = filteredChannelIds.filter(
        filterBySelectedChannelTypeFilter({
          channels,
          selectedChannelTypeFilter,
        }),
      );
    }

    if (selectedChannelTypeFilter === CHANNEL_FILTER_TYPES.DIRECT_CHATS) {
      filteredChannelIds = filteredChannelIds.filter(filterByBrand({ channelBrand: selectedChannelBrand }));
    }

    if (selectedChannelTypeFilter === CHANNEL_FILTER_TYPES.TEAM_CHATS) {
      filteredChannelIds = filteredChannelIds.filter(
        filterBySelectedSalesSpaceChannelFilter({
          isAgent,
          selectedSalesSpaceChannelFilter,
          channelMembers,
          members,
          currentUserId,
          users,
          isConnected,
        }),
      );
    }

    // Filter archived channels only while not searching
    if (!searchInput) {
      filteredChannelIds = filteredChannelIds.filter(
        filterByHiddenStatus({
          currentUser,
        }),
      );
    }

    const unreadMessagesFilter =
      selectedChannelTypeFilter === CHANNEL_FILTER_TYPES.TEAM_CHATS
        ? unreadSalesSpaceMessagesFilterSelected
        : unreadMessagesFilterSelected;
    if (unreadMessagesFilter === UnreadMessagesFilter.UNREAD) {
      filteredChannelIds = filteredChannelIds.filter(
        filterByUnreadMessages({
          channels,
          currentUserId,
          members,
          channelMembers,
        }),
      );
    }

    const sortingDirection =
      selectedChannelTypeFilter === CHANNEL_FILTER_TYPES.TEAM_CHATS
        ? selectedSalesSpaceChannelOrder
        : selectedDirectChannelOrder;
    return sortChannels({
      sortingDirection,
      channelIds: filteredChannelIds,
      channels,
      currentUserId,
      channelMembers,
      members,
      users,
      isAgent,
      channelNotificationDates,
    });
  },
);
